/*!
 * CHIEF - Cybersecurity Learning Platform (PROPRIETARY SOFTWARE)
 * Notification Context
 * 
 * Copyright (c) 2025 Srivatsan Sk. All rights reserved.
 * 
 * PROPRIETARY LICENSE NOTICE:
 * This software is the exclusive proprietary property of Srivatsan Sk.
 * Licensed to client under strict commercial license agreement.
 * Unauthorized use, copying, modification, distribution, or removal
 * of copyright notices is strictly prohibited.
 * 
 * Original Author: Srivatsan Sk
 * License: Proprietary/Commercial
 * Status: Confidential
 * 
 * WARNING: This code contains proprietary business logic and algorithms.
 * Reverse engineering, decompilation, or derivative works are prohibited.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/apiClient';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetchTime, setLastFetchTime] = useState(null);

    // Refs for managing timeouts and preventing memory leaks
    const fetchTimeoutRef = useRef(null);

    // Get authentication status
    const { currentUser, loading: authLoading } = useAuth();

    // Fetch notifications from API (only if authenticated)
    const fetchNotifications = useCallback(async (limit = 50, include_read = true, force = false) => {
        // Don't fetch if user is not authenticated
        if (!currentUser) {
            console.warn('Cannot fetch notifications: user not authenticated');
            setNotifications([]);
            setUnreadCount(0);
            return;
        }

        // Avoid redundant calls unless forced (wait at least 5 minutes between calls)
        const now = Date.now();
        if (!force && lastFetchTime && (now - lastFetchTime < 300000)) {
            console.log('Skipping fetch - too recent (less than 5 minutes ago)');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            console.log(`Fetching notifications (limit: ${limit}, include_read: ${include_read}, force: ${force})...`);
            const response = await api.notifications.getRecent(limit, include_read);
            console.log('Fetch notifications response:', response);

            if (response && response.notifications) {
                setNotifications(response.notifications);
                setUnreadCount(response.unread_count || 0);
                setLastFetchTime(now);
            } else if (Array.isArray(response)) {
                // Fallback if response structure is different
                setNotifications(response);
                setUnreadCount(response.filter(n => !n.is_read).length);
                setLastFetchTime(now);
            } else {
                console.warn('Unexpected notification response structure:', response);
                setNotifications([]);
                setUnreadCount(0);
            }
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError('Failed to fetch notifications');
            // Set empty state on error
            setNotifications([]);
            setUnreadCount(0);
        } finally {
            setLoading(false);
        }
    }, [currentUser, lastFetchTime]);

    // Mark a single notification as read (only if authenticated)
    const markAsRead = useCallback(async (notificationId) => {
        if (!currentUser) {
            console.warn('Cannot mark notification as read: user not authenticated');
            return false;
        }

        try {
            console.log(`Marking notification ${notificationId} as read...`);
            const response = await api.notifications.markAsRead(notificationId);
            console.log('Mark as read response:', response);

            // Update local state
            setNotifications(prev =>
                prev.map(notification =>
                    notification.id === notificationId
                        ? { ...notification, is_read: true, read_at: new Date().toISOString() }
                        : notification
                )
            );

            // Update unread count
            setUnreadCount(prev => Math.max(0, prev - 1));

            return true;
        } catch (err) {
            console.error('Error marking notification as read:', err);
            setError('Failed to mark notification as read');
            return false;
        }
    }, [currentUser]);

    // Mark all notifications as read (only if authenticated)
    const markAllAsRead = useCallback(async () => {
        if (!currentUser) {
            console.warn('Cannot mark all notifications as read: user not authenticated');
            return false;
        }

        try {
            console.log('Marking all notifications as read...');
            const response = await api.notifications.markAllAsRead();
            console.log('Mark all as read response:', response);

            // Update local state
            setNotifications(prev =>
                prev.map(notification => ({
                    ...notification,
                    is_read: true,
                    read_at: new Date().toISOString()
                }))
            );

            setUnreadCount(0);
            return true;
        } catch (err) {
            console.error('Error marking all notifications as read:', err);
            setError('Failed to mark all notifications as read');
            return false;
        }
    }, [currentUser]);

    // Delete a notification (only if authenticated)
    const deleteNotification = useCallback(async (notificationId) => {
        if (!currentUser) {
            console.warn('Cannot delete notification: user not authenticated');
            return false;
        }

        try {
            console.log(`Deleting notification ${notificationId}...`);
            const response = await api.notifications.deleteNotification(notificationId);
            console.log('Delete notification response:', response);

            // Update local state
            const notificationToDelete = notifications.find(n => n.id === notificationId);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));

            // Update unread count if the deleted notification was unread
            if (notificationToDelete && !notificationToDelete.is_read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }

            return true;
        } catch (err) {
            console.error('Error deleting notification:', err);
            setError('Failed to delete notification');
            return false;
        }
    }, [currentUser, notifications]);

    // Add a new notification (for real-time updates)
    const addNotification = useCallback((notification) => {
        setNotifications(prev => [notification, ...prev]);
        if (!notification.is_read) {
            setUnreadCount(prev => prev + 1);
        }
    }, []);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Force refresh notifications (ignores timing restrictions)
    const forceRefreshNotifications = useCallback(() => {
        console.log('Force refreshing notifications...');
        fetchNotifications(50, true, true);
    }, [fetchNotifications]);

    // Refresh notifications if enough time has passed (for dashboard visits)
    const refreshNotificationsIfNeeded = useCallback(() => {
        const now = Date.now();
        // Only refresh if it's been more than 5 minutes since last fetch
        if (!lastFetchTime || (now - lastFetchTime > 300000)) {
            console.log('Refreshing notifications for page visit...');
            fetchNotifications(50, true, false);
        } else {
            console.log('Notifications are fresh enough, skipping refresh');
        }
    }, [fetchNotifications, lastFetchTime]);

    // Auto-fetch notifications only on initial login
    useEffect(() => {
        // Only fetch when user first becomes authenticated (initial login)
        if (!authLoading && currentUser) {
            console.log('User authenticated, fetching initial notifications...');
            // Only fetch if we don't have any notifications yet
            if (notifications.length === 0) {
                fetchNotifications(50, true, true);
            }
        }

        // Clear notifications when user logs out
        if (!authLoading && !currentUser) {
            console.log('User not authenticated, cleaning up notifications...');
            setNotifications([]);
            setUnreadCount(0);
            setError(null);
            setLastFetchTime(null);
        }

        // Cleanup function
        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, [currentUser, authLoading]); // Remove fetchNotifications dependency to prevent loops    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, []);

    const value = {
        notifications,
        unreadCount,
        loading,
        error,
        lastFetchTime,
        fetchNotifications,
        forceRefreshNotifications,
        refreshNotificationsIfNeeded,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        addNotification,
        clearError
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};